import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { Chart, LineController, LineElement, PointElement, LinearScale, CategoryScale, Filler } from 'chart.js';

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Filler);

/** Mini-gráfica de línea sin ejes/leyenda, usada bajo los KPI cards e insights. */
@Component({
    selector: 'app-sparkline',
    template: `<canvas #canvas></canvas>`,
    styles: [`
        :host { display: block; width: 100%; height: 36px; }
        canvas { width: 100% !important; height: 100% !important; }
    `]
})
export class SparklineComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() data: number[] = [];
    @Input() color = '#7c3aed';

    @ViewChild('canvas') canvasRef?: ElementRef<HTMLCanvasElement>;

    private chart: Chart<'line'> | null = null;

    ngAfterViewInit(): void {
        this.render();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['data'] && !changes['color']) return;
        this.render();
    }

    ngOnDestroy(): void {
        this.chart?.destroy();
    }

    private render(): void {
        const canvas = this.canvasRef?.nativeElement;
        if (!canvas) return;
        const points = this.data && this.data.length ? this.data : [0, 0, 0, 0, 0];

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        if (this.chart) {
            this.chart.data.labels = points.map((_, i) => String(i));
            this.chart.data.datasets[0].data = points;
            (this.chart.data.datasets[0] as any).borderColor = this.color;
            (this.chart.data.datasets[0] as any).backgroundColor = this.fillGradient(ctx, canvas.clientHeight || 37);
            this.chart.update();
            return;
        }

        this.chart = new Chart(ctx, {
            type: 'line',
            data: {
                labels: points.map((_, i) => String(i)),
                datasets: [{
                    data: points,
                    borderColor: this.color,
                    backgroundColor: this.fillGradient(ctx, canvas.clientHeight || 37),
                    borderWidth: 2,
                    pointRadius: 0,
                    tension: 0.35,
                    fill: true
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                plugins: { legend: { display: false }, tooltip: { enabled: false } },
                scales: {
                    x: { display: false },
                    y: { display: false }
                }
            }
        });
    }

    /** Degradado del color de la línea (más saturado arriba) a transparente
     * abajo, como en el diseño — un relleno sólido a un solo tono se ve
     * plano comparado con Figma. */
    private fillGradient(ctx: CanvasRenderingContext2D, height: number): CanvasGradient {
        const gradient = ctx.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, this.hexToRgba(this.color, 0.35));
        gradient.addColorStop(1, this.hexToRgba(this.color, 0));
        return gradient;
    }

    private hexToRgba(hex: string, alpha: number): string {
        const clean = hex.replace('#', '');
        const bigint = parseInt(clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
}
